import styled from '@emotion/styled'
import { colors, LoadingDots, spacing, Text, Tooltip } from '@retailer-platform/shared-components'
import React, { type FunctionComponent } from 'react'

export interface InsightsMetricsTrendMetricProps {
  title: string
  valueFormatFn: (val: number) => string
  value: number
  comparisonValue: number
  desiredDirection: 'positive' | 'negative'
  isLoading: boolean
  tooltip: string
  color: string
  variant?: 'column'
}

const Container = styled.div({
  minWidth: 100,
  paddingRight: spacing.X16,
})

const getChangeColor = ({
  value,
  comparisonValue,
  desiredDirection,
}: Pick<InsightsMetricsTrendMetricProps, 'value' | 'comparisonValue' | 'desiredDirection'>) => {
  const absoluteChange = value - comparisonValue

  const valueImproved =
    (absoluteChange > 0 && desiredDirection === 'positive') ||
    (absoluteChange < 0 && desiredDirection === 'negative')

  if (valueImproved) return colors.HIGHLIGHT.REGULAR

  const valueWorsened =
    (absoluteChange > 0 && desiredDirection === 'negative') ||
    (absoluteChange < 0 && desiredDirection === 'positive')

  if (valueWorsened) return colors.DETRIMENTAL.REGULAR

  return colors.GRAYSCALE.X50
}

const PercentChange: FunctionComponent<
  Pick<InsightsMetricsTrendMetricProps, 'value' | 'comparisonValue' | 'desiredDirection'>
> = ({ value, comparisonValue, desiredDirection }) => {
  const absoluteChange = value - comparisonValue

  if (!comparisonValue || Number.isNaN(absoluteChange)) return null

  const prefix = absoluteChange > 0 ? '+' : ''

  const percentageChange = ((absoluteChange / comparisonValue) * 100).toFixed(2)

  const changeColor = getChangeColor({ value, comparisonValue, desiredDirection })

  return (
    <Text
      size="small"
      weight="semibold"
      css={{ lineHeight: '12px', color: changeColor, paddingTop: '6px' }}
      data-testid="metrics-trend-metric-percent-change"
    >{`${prefix}${percentageChange}%`}</Text>
  )
}

export const InsightsMetricsTrendMetric: FunctionComponent<InsightsMetricsTrendMetricProps> = ({
  title,
  valueFormatFn,
  value,
  comparisonValue,
  desiredDirection,
  isLoading,
  tooltip,
  color,
  variant,
}) => {
  if (isLoading) {
    return (
      <Container css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoadingDots testId="trend-metric-loading-dots" height={12} />
      </Container>
    )
  }
  return (
    <Container>
      <div css={{ display: 'flex', alignItems: 'center' }}>
        {tooltip ? (
          <Tooltip
            placement="bottom"
            target={
              <Text
                size="small"
                weight="regular"
                css={{ whiteSpace: 'nowrap', borderBottom: `1px dashed ${colors.GRAYSCALE.X50}` }}
              >
                {title}
              </Text>
            }
          >
            {tooltip}
          </Tooltip>
        ) : (
          <div>{title}</div>
        )}
        <div
          css={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: color,
            marginLeft: 4,
          }}
          data-testid="metrics-trend-metric-legend-color"
        />
      </div>

      <div
        css={{
          display: 'flex',
          alignItems: variant === 'column' ? 'flex-start' : 'flex-end',
          marginTop: 8,
          flexDirection: variant === 'column' ? 'column' : 'row',
        }}
      >
        <Text size="large" weight="semibold" css={{ lineHeight: '18px' }}>
          {valueFormatFn(value)}
        </Text>
        <PercentChange
          value={value}
          comparisonValue={comparisonValue}
          desiredDirection={desiredDirection}
        />
      </div>
    </Container>
  )
}
